import { Filter } from '../../grid/gridFilters/types';
import { ColumnKeys as AssetsColumnKeys } from '../assetsInventoryGrid/types';
import { ColumnKeys as ArtifactsColumnKeys } from '../artifactsInventoryGrid/types';
import { FilterType } from '../widgets/genericTopData/types/types';
import {
  Assignee,
  COLUMN_RELATED_FINDINGS_CATEGORY_LIMIT,
  COLUMN_TO_FINDING_TYPE_MAP,
  Events,
  FindingClassType,
  findingTypeToIconMap,
  RELATED_ARTIFACTS_COLUMN_ORDER,
  RELATED_ASSETS_COLUMN_ORDER,
  UNAVAILABLE_DATA,
} from '../types';
import { GzTooltipWebComponent } from '../../ui/gzTooltip';
import { EASMIconSet } from '../types/iconSet';
import { BaseWebComponentTranslator } from '../../framework/BaseWebComponentTranslator';
import { render } from 'lit';
import { AssignedUserType, InvestigationPriority, InvestigationStatus } from '../../investigation/types';
import { translateFn } from '../../utils/translator.helper';
import { GzLinkWebComponent } from '../../ui/gzLinkComponent';
import { GzSelectWebComponent } from '../../ui/gzSelect';
import { SelectEvents } from '../../ui/gzSelect/types';
import { WebComponentEvent } from '../../framework/BaseWebComponentController';
import { Property } from '../bulkChangeProperty/types';
import { htmlDecode } from '../../utils/escape.utils';

export function handleWidgetWithTopDataResize() {
  if (!this.parentElement || !this.gzEASMGenericTopData) {
    return;
  }

  const paddingTop = 24 + 32 + 24;
  const paddingBottom = 32 + 24 + 16 + 8;
  const rowHeight = 32;

  const availableView = this.parentElement.offsetHeight;

  this.gzEASMGenericTopData.numberOfRowsToDisplay = Math.floor(
    (availableView - paddingTop - paddingBottom) / rowHeight
  );
  this.gzEASMGenericTopData.rerender();
}

// ToDo: change method to also work with the artifacts inventory grid (commented parts from the below method method)
export function generateFiltersForInventory(
  filterType: FilterType,
  filterData: any,
  companyId: string,
  filterDataArray: Array<any> = []
): Filter<AssetsColumnKeys | ArtifactsColumnKeys>[] {
  const filters: Filter<AssetsColumnKeys | ArtifactsColumnKeys>[] = [];
  filters.push({
    [AssetsColumnKeys.COMPANY]: {
      exactMatch: companyId,
    },
  });

  if (!filterData && filterDataArray.length === 0) {
    return filters;
  }

  switch (filterType) {
    case FilterType.PORT:
      filters.push({
        [ArtifactsColumnKeys.SERVICES_PORT]: {
          eq: Number(filterData),
        },
      });
      break;
    case FilterType.CVES:
      filters.push({
        [ArtifactsColumnKeys.SERVICES_CVES]: {
          complexEnumFilter: [
            {
              label: filterData,
              value: filterData,
            },
          ],
        },
      });
      break;
    case FilterType.FINDING_NAME:
      filters.push({
        [AssetsColumnKeys.NAME]: {
          complexEnumFilter: [
            {
              label: filterData,
              value: filterData,
            },
          ],
        },
      });
      break;
    case FilterType.PROTOCOL:
      filters.push({
        [ArtifactsColumnKeys.SERVICES_PROTOCOL]: {
          startsWith: filterData,
        },
      });
      break;
    case FilterType.FINDING_TYPE:
      filters.push({
        [AssetsColumnKeys.TYPE]: {
          in: filterDataArray,
        },
      });
      break;
    case FilterType.COUNTRY:
      filters.push({
        [ArtifactsColumnKeys.COUNTRY]: {
          in: filterDataArray,
        },
      });
      break;
    case FilterType.EXPIRATION_STATUS:
      filters.push({
        [ArtifactsColumnKeys.CERTIFICATE_REPORTS_EXPIRATION_STATUS]: {
          in: filterDataArray,
        },
      });
      break;
    case FilterType.RECORD_TYPE:
      filters.push({
        [ArtifactsColumnKeys.DNS_RECORDS_TYPE]: {
          in: filterDataArray,
        },
      });
      break;
    case FilterType.ASSET_STATUS:
      filters.push({
        [AssetsColumnKeys.NEXT_SCAN_STATUS]: {
          in: filterDataArray,
        },
      });
      break;
    case FilterType.VULNERABLE_TO_ZONE_TRANSFER:
      filters.push({
        [ArtifactsColumnKeys.VULNERABLE_TO_ZONE_TRANSFER]: {
          in: filterDataArray,
        },
      });
      break;
    case FilterType.ASSET_RELATED_TO_ASSET:
      filters.push({
        [AssetsColumnKeys.ASSETS_RELATED_TO_ASSET]: {
          complexEnumFilter: filterData,
        },
      });
      break;
    case FilterType.ARTIFACTS_RELATED_TO_ASSET:
      filters.push({
        [ArtifactsColumnKeys.ASSETS_RELATED_TO_ARTIFACT]: {
          complexEnumFilter: filterData,
        },
      });
      break;
    case FilterType.ASSETS_RELATED_TO_ARTIFACT:
      filters.push({
        [AssetsColumnKeys.ARTIFACTS_RELATED_TO_ASSET]: {
          complexEnumFilter: filterData,
        },
      });
      break;
    default:
      break;
  }

  return filters;
}

export function relatedFindingsRenderer(
  rowData: any,
  findingType: FindingClassType,
  relatedFindingType: FindingClassType
): HTMLElement {
  const tooltipComponent = new GzTooltipWebComponent();
  const tooltipText: Array<string> = [];

  tooltipComponent.classList.add('cell-label');
  tooltipComponent.classList.add('related-assets-cell');
  tooltipComponent.setAttribute('part', 'item-group-icons');

  let RELATED_FINDING_COLUMN_ORDER: string[] = [];
  let itemLabel = '';

  if (relatedFindingType === FindingClassType.ARTIFACT) {
    RELATED_FINDING_COLUMN_ORDER = RELATED_ARTIFACTS_COLUMN_ORDER;
    itemLabel = 'artifactsRelatedToAsset';
  }

  if (relatedFindingType === FindingClassType.ASSET) {
    RELATED_FINDING_COLUMN_ORDER = RELATED_ASSETS_COLUMN_ORDER;
    itemLabel = findingType === FindingClassType.ASSET ? 'assetsRelatedToAsset' : 'assetsRelatedToArtifact';
  }

  if (
    !rowData.item[itemLabel] ||
    (rowData.item[itemLabel] && Object.values(rowData.item[itemLabel]).every((value: number) => value === 0))
  ) {
    tooltipComponent.innerText = UNAVAILABLE_DATA;
  } else {
    RELATED_FINDING_COLUMN_ORDER.forEach((relatedFindingsCategory: string) => {
      const nrOfRelatedFindingsPerCategory = rowData.item[itemLabel][relatedFindingsCategory];
      const isBiggerThanLimit = nrOfRelatedFindingsPerCategory > COLUMN_RELATED_FINDINGS_CATEGORY_LIMIT;

      if (nrOfRelatedFindingsPerCategory === 0) {
        return;
      }

      const relatedFindingsCategoryElement = document.createElement('div');
      relatedFindingsCategoryElement.style.paddingRight = 'var(--spacer)';
      relatedFindingsCategoryElement.setAttribute('part', 'grid-cell-icon');
      relatedFindingsCategoryElement.id = relatedFindingsCategory;
      render(
        EASMIconSet.createIconSet(findingTypeToIconMap[COLUMN_TO_FINDING_TYPE_MAP[relatedFindingsCategory]]),
        relatedFindingsCategoryElement
      );

      const categoryTextElement = document.createElement('div');
      categoryTextElement.style.marginLeft = 'calc(-1 * var(--spacer))';
      categoryTextElement.style.fontFamily = 'var(--font-family-secondary)';
      categoryTextElement.innerText = isBiggerThanLimit ? '99+' : nrOfRelatedFindingsPerCategory;
      tooltipText.push(
        BaseWebComponentTranslator.getInstance().translateEASMType(
          COLUMN_TO_FINDING_TYPE_MAP[relatedFindingsCategory]
        ) +
          ': ' +
          nrOfRelatedFindingsPerCategory
      );

      relatedFindingsCategoryElement.appendChild(categoryTextElement);
      tooltipComponent.appendChild(relatedFindingsCategoryElement);
    });
  }

  tooltipComponent.tooltipText = tooltipText.join(', ');

  return tooltipComponent;
}

export function investigationStatusCellRenderer(rowData: any): HTMLDivElement {
  const investigationStatusCell: HTMLDivElement = document.createElement('div');

  investigationStatusCell.innerText = this.stringsTranslator.translateInvestigationStatus(
    rowData.item.metadata.investigation.status
  );
  investigationStatusCell.className = 'cell-label';

  return investigationStatusCell;
}

export function assigneeCellRenderer(rowData: any): HTMLDivElement {
  const assigneeCell: HTMLDivElement = document.createElement('div');

  assigneeCell.innerText = htmlDecode(assigneeRenderer(
    rowData.item.assignee,
    this.dependencies.getUser().company.id,
    this.stringsTranslator
  ));
  assigneeCell.className = 'cell-label';

  return assigneeCell;
}

export function assigneeRenderer(
  assignee: Assignee | null,
  currentCompanyId: string,
  translator: BaseWebComponentTranslator
): string {
  if (null === assignee) {
    return translator.translateInvestigationAssignedUserType(AssignedUserType.UNASSIGNED);
  }

  if (assignee.company && assignee.company.id !== currentCompanyId) {
    return translateFn(
      '{companyName} ({userName})',
      {companyName: assignee.company.name, userName: assignee.profile.fullName}
    );
  }

  return assignee.profile.fullName;
}

export function priorityCellRenderer(rowData: any): HTMLDivElement {
  const priorityCell: HTMLDivElement = document.createElement('div');

  priorityCell.innerText = this.stringsTranslator.translateInvestigationPriority(
    rowData.item.metadata.investigation.priority
  );
  priorityCell.className = 'cell-label';

  return priorityCell;
}

export function notesCellRenderer(rowData: any): GzLinkWebComponent {
  const notesCell = new GzLinkWebComponent();

  notesCell.innerText = 0 === rowData.item.metadata.investigation.notesCount
    ? translateFn('Add note')
    : rowData.item.metadata.investigation.notesCount;
  notesCell.className = 'cell-label';
  notesCell.callback = (): void => this.dom.$detailsPanel.setNotesInContent(rowData.item._id);

  notesCell.updateComplete.then((): void => {
    (notesCell.shadowRoot?.querySelector('.wrapper') as HTMLDivElement).style.padding = '0';
  });

  return notesCell;
}

const propertiesToOptionsMapping = {
    [Property.INVESTIGATION_STATUS]: 'investigationStatusOptions',
    [Property.ASSIGNEE]: 'assigneeOptions',
    [Property.PRIORITY]: 'priorityOptions',
    [Property.SCAN_ACTION]: 'scanActionOptions',
  },
  selectedOptionValueFetchers = {
    [Property.INVESTIGATION_STATUS]: function (rowData: any): InvestigationStatus {
      return rowData.item.metadata.investigation.status;
    },
    [Property.ASSIGNEE]: function (rowData: any): AssignedUserType | string | null {
      if (null === rowData.item.assignee) {
        return AssignedUserType.UNASSIGNED;
      } else if (!rowData.item.assignee._id
        || (this.dependencies.getUser().company.id !== rowData.item.assignee.company.id)
      ) {
        return null;
      }

      return this.dependencies.getUser()._id === rowData.item.assignee._id
        ? AssignedUserType.CURRENT_USER
        : rowData.item.assignee._id;
    },
    [Property.PRIORITY]: function (rowData: any): InvestigationPriority {
      return rowData.item.metadata.investigation.priority;
    },
  };

export function changePropertyActionsRenderer(rowData: any): GzSelectWebComponent {
  const select = new GzSelectWebComponent();

  select.options = [
    {
      title: translateFn('Investigation status'),
      value: Property.INVESTIGATION_STATUS,
    },
    {
      title: translateFn('Assign'),
      value: Property.ASSIGNEE,
    },
    {
      title: translateFn('Prioritize'),
      value: Property.PRIORITY,
    }
  ];

  if (rowData.item.class === FindingClassType.ASSET) {
    select.options.unshift({
      title: translateFn('Scan action'),
      value: Property.SCAN_ACTION,
    });
  }

  select.classList.add('d-flex', 'align-items-center', 'h-full');
  select.innerHTML = `<gz-icon-button
    icon="ds-icon-more"
    size="24"
    fontSize="22"
    buttonType="square"
    buttonFill="quiet"
    buttonColor="secondary"
    class="inline-menu-trigger"
  />`;

  select.addEventListener(SelectEvents.SELECTION_CHANGED, (event: WebComponentEvent): void => {
    this.dom.$gridView.dispatchEvent(
      new CustomEvent(
        Events.CHANGE_PROPERTY,
        {
          detail: {
            property: event.detail.selected,
            findingsClassType: rowData.item.class,
            findingIds: [rowData.item._id],
            options: this[propertiesToOptionsMapping[event.detail.selected]],
            selectedOptionValue: selectedOptionValueFetchers[event.detail.selected] ? selectedOptionValueFetchers[event.detail.selected].call(this, rowData) : null,
          },
        }
      )
    );
  });

  return select;
}
